import { berlinDateString } from '../../../../kits/berlinDateKit';
import type { UserStatus } from '../../../../shared-state/identity/types';

// localStorage: Berlin date (YYYY-MM-DD) the user last confirmed their out-of-office. signOut() clears
// localStorage, so the reminder is asked again after every sign-in (e.g. after the lobby lockout).
export const OOO_STATUS_REMINDER_ACK_KEY = 'oooStatusReminderAcknowledged';

export function readOooStatusReminderAck(): string | null {
    try {
        return localStorage.getItem(OOO_STATUS_REMINDER_ACK_KEY);
    } catch {
        return null;
    }
}

/** The user chose (or kept) out of office today: don't ask again today. */
export function acknowledgeOooStatusReminder(today: string = berlinDateString()): void {
    try {
        localStorage.setItem(OOO_STATUS_REMINDER_ACK_KEY, today);
    } catch {
        // storage unavailable: asked again on the next page load
    }
}

/** Ask again on the next area entry (e.g. after an automatic move to the lobby). */
export function clearOooStatusReminderAck(): void {
    try {
        localStorage.removeItem(OOO_STATUS_REMINDER_ACK_KEY);
    } catch {
        // storage unavailable
    }
}

/** Berlin calendar date (YYYY-MM-DD) of an out-of-office return date, or null if missing/invalid. */
export function oooReturnDateString(returnDate: Date | string | null | undefined): string | null {
    if (!returnDate) return null;
    if (typeof returnDate === 'string') return /^\d{4}-\d{2}-\d{2}/.test(returnDate) ? returnDate.slice(0, 10) : null;
    return Number.isNaN(returnDate.getTime()) ? null : berlinDateString(returnDate);
}

/** The return date is a past day (the time of day is ignored). */
export function isOooReturnDatePast(returnDate: Date | string | null | undefined, today: string): boolean {
    const returnDateStr = oooReturnDateString(returnDate);
    return returnDateStr !== null && returnDateStr < today;
}

export interface IOooStatusReminderInput {
    status: UserStatus | undefined;
    /** Today's Berlin date, YYYY-MM-DD. */
    today: string;
    inArea: boolean;
    /** An OOO the idle chain set returns to available on activity by itself. */
    idleChainStage: string | null;
    presencePromptOpen: boolean;
    acknowledgedOn: string | null;
}

/**
 * Checked on entering an area (never on a status change): still out of office → ask once a day,
 * and again after each sign-in or automatic lobby move.
 */
export function shouldShowOooStatusReminder(input: IOooStatusReminderInput): boolean {
    const { status, today, inArea, idleChainStage, presencePromptOpen, acknowledgedOn } = input;
    if (status !== 'out-of-office' || !inArea || idleChainStage === 'ooo' || presencePromptOpen) return false;
    return acknowledgedOn !== today;
}
